/**
 * Registro único de colas y vistas del dominio Pedidos (ADR 0029).
 *
 * POR QUÉ EXISTE: cada pantalla de Operaciones definía su propio filtro por dentro
 * (`VARIANTS` en OperationsOrdersView). El resultado fue que cinco entradas del menú terminaron
 * siendo cinco filtros del mismo query, tres de ellas subconjuntos anidados de la misma:
 * `Cobranzas ⊂ Entregas ⊂ Ventas`. Nadie lo vio porque no había un lugar donde se pudiera ver.
 *
 * La distinción que el archivo hace cumplir:
 *
 *   COLA  — entrada de menú. Las colas son MUTUAMENTE EXCLUYENTES: un pedido está en una y solo
 *           una. Se separan por `status`, que es el compromiso.
 *   VISTA — filtro dentro de una cola (los tabs). Pueden solaparse entre sí libremente; lo que no
 *           pueden es escaparse de su cola.
 *
 * `queues.test.ts` verifica las dos propiedades sobre TODAS las combinaciones posibles de estado.
 * Mientras ese test pase, es imposible reintroducir el problema que originó el ADR.
 */

import type { OrderLifecycleStage, OrderShippingStatus } from '@/types/operations.types';

export type OrderStatus = 'draft' | 'open' | 'closed' | 'cancelled';
export type PaymentStatus = 'unpaid' | 'partial' | 'paid' | 'refunded';
export type FulfillmentStatus = 'unfulfilled' | 'partial' | 'fulfilled' | 'returned';

/** Las tres dimensiones que definen dónde cae un pedido. El canal NO es una de ellas: es filtro. */
export interface EstadoDelPedido {
  status: OrderStatus;
  payment_status: PaymentStatus;
  fulfillment_status: FulfillmentStatus;
  /** Saldo pendiente. Derivado de grand_total - amount_paid por la propia base. */
  amount_due: number;
}

export const TODOS_LOS_STATUS: readonly OrderStatus[] = ['draft', 'open', 'closed', 'cancelled'];
export const TODOS_LOS_PAYMENT: readonly PaymentStatus[] = ['unpaid', 'partial', 'paid', 'refunded'];
export const TODOS_LOS_FULFILLMENT: readonly FulfillmentStatus[] = ['unfulfilled', 'partial', 'fulfilled', 'returned'];

export interface Vista {
  id: string;
  label: string;
  /** Predicado dentro de la cola. No puede mirar `status`: eso lo decide la cola. */
  incluye: (e: EstadoDelPedido) => boolean;
}

export interface Cola {
  id: string;
  label: string;
  /** Los status que pertenecen a esta cola. La unión de todas las colas cubre TODOS_LOS_STATUS
   *  y la intersección entre dos cualesquiera es vacía — eso lo verifica el test. */
  status: readonly OrderStatus[];
  vistas: readonly Vista[];
}

/**
 * Las colas. Tres, no seis.
 *
 * 🪤 «Entregas» NO es una cola de pedidos. Es un módulo aparte porque su objeto es OTRO
 *    (`order_shipments`: guía, courier, tracking, costo real). Modelarla acá como una cola de
 *    pedidos filtrada por estado de envío es exactamente el error que este ADR corrige: era un
 *    filtro de pedidos disfrazado de módulo, y por eso se sentía duplicada con Ventas.
 */
export const COLAS: readonly Cola[] = [
  {
    id: 'por_confirmar',
    label: 'Por confirmar',
    status: ['draft'],
    vistas: [
      { id: 'todos', label: 'Todos', incluye: () => true },
      { id: 'sin_pago', label: 'Sin pago', incluye: (e) => e.payment_status === 'unpaid' },
      { id: 'con_adelanto', label: 'Con adelanto', incluye: (e) => e.payment_status === 'partial' },
    ],
  },
  {
    id: 'ventas',
    label: 'Ventas',
    status: ['open', 'closed'],
    vistas: [
      { id: 'todas', label: 'Todas', incluye: () => true },
      { id: 'por_cobrar', label: 'Por cobrar', incluye: (e) => e.amount_due > 0 },
      { id: 'pagadas', label: 'Pagadas', incluye: (e) => e.payment_status === 'paid' },
      { id: 'por_despachar', label: 'Por despachar', incluye: (e) => e.fulfillment_status === 'unfulfilled' },
      { id: 'entregadas', label: 'Entregadas', incluye: (e) => e.fulfillment_status === 'fulfilled' },
    ],
  },
  {
    id: 'canceladas',
    label: 'Canceladas',
    status: ['cancelled'],
    vistas: [
      { id: 'todas', label: 'Todas', incluye: () => true },
      { id: 'rechazo_puerta', label: 'Rechazo en puerta', incluye: (e) => e.fulfillment_status === 'returned' },
      { id: 'con_adelanto', label: 'Con adelanto a devolver', incluye: (e) => e.payment_status === 'partial' },
    ],
  },
] as const;

/** En qué cola cae un pedido. Devuelve null solo si el status es desconocido. */
/**
 * Los `status` que significan «se vendió», derivados de la cola Ventas de arriba.
 *
 * 🪤 SE DERIVA, NO SE ESCRIBE. La primera versión de la columna «Venta / Presupuesto» de la ficha
 * del cliente traía su propia lista `['sale','fulfilled','closed']` sobre el enum de etapas que
 * ADR 0029 está retirando —justamente porque colapsa cuatro dimensiones—. El gate del ADR la
 * frenó, y con razón: el compromiso lo dice `status`, y la cola Ventas ya lo tenía definido. Una
 * lista más habría sido la quinta definición de «venta» en el repo.
 */
export const STATUS_DE_VENTA: readonly OrderStatus[] =
  COLAS.find((c) => c.id === 'ventas')?.status ?? ['open', 'closed'];

/** ¿Este pedido llegó a ser una venta, o sigue siendo un presupuesto? */
export function esVenta(status: string | null | undefined): boolean {
  return STATUS_DE_VENTA.includes(status as OrderStatus);
}

export function colaDe(e: EstadoDelPedido): Cola | null {
  return COLAS.find((c) => c.status.includes(e.status)) ?? null;
}

/** Todas las colas que reclaman este pedido. Debe tener SIEMPRE largo 1 — lo prueba el test. */
export function colasQueReclaman(e: EstadoDelPedido): Cola[] {
  return COLAS.filter((c) => c.status.includes(e.status));
}

// ════════════════════════════════════════════════════════════════════════════════════════════════
// El puente al modelo viejo (ADR 0029, expand→migrate→contract). Se borra entero en F6.
// ════════════════════════════════════════════════════════════════════════════════════════════════

/**
 * Cómo se dice cada cola en `lifecycle_stage`, el enum que el ADR retira.
 *
 * ⭐ ESTE ES EL ÚNICO LUGAR DEL PANEL QUE CONOCE LA TRADUCCIÓN. Antes cada pantalla llevaba su
 * propio `stages: ['sale','fulfilled','closed']` adentro, y por eso nadie podía ver que tres de
 * ellas eran subconjuntos anidados de la misma consulta: había que abrir cinco archivos y
 * compararlos de memoria.
 *
 * 🪤 Cuando se borre `lifecycle_stage` (F6), lo que hay que cambiar es ESTE archivo y las cinco
 *    funciones SQL que reciben `p_stages`. Ninguna pantalla más.
 */
export const STAGES_LEGACY: Record<string, readonly OrderLifecycleStage[]> = {
  por_confirmar: ['quotation', 'online'],
  ventas: ['sale', 'fulfilled', 'closed'],
  canceladas: ['cancelled'],
} as const;

/**
 * ¿Las líneas de un pedido se editan? En presupuesto o en venta; despachado, cerrado, cancelado o un
 * pedido online sin confirmar, son de lectura.
 *
 * 🪤 VA EN EL PUENTE Y NO SOBRE `status`: medido en producción el 5-oct-2026, la etapa `sale` incluye
 *    ventas ya entregadas (27) y devueltas (2). Traducirla a «open + sin despachar» cambiaría qué se
 *    edita, y eso se decide a la vista en F6, no de pasada.
 */
export const ETAPAS_QUE_EDITAN_LINEAS: readonly OrderLifecycleStage[] = ['quotation', 'sale'];

export function lasLineasSeEditan(etapa: string | null | undefined): boolean {
  return ETAPAS_QUE_EDITAN_LINEAS.includes(etapa as OrderLifecycleStage);
}

/**
 * Lo que cada PANTALLA le pide a la base hoy.
 *
 * Una pantalla es una cola, y —cuando corresponde— una vista de esa cola. Que dos pantallas
 * declaren la misma cola no es duplicación: es que miran el mismo conjunto por razones distintas,
 * y eso ahora se ve de un vistazo en vez de estar repartido en cinco archivos.
 *
 * 🪤 DOS PANTALLAS NO PUEDEN USAR TODAVÍA LAS ETAPAS DE SU COLA, y hay que decir por qué:
 *
 *    · `logistica` es la cola `ventas` (`['sale','fulfilled','closed']`) pero pide
 *      `['sale','fulfilled']`. Si le diéramos la lista completa, entrarían los 11.535 pedidos
 *      `closed` —el 74 % de la base— y «Por despachar» quedaría inutilizable.
 *
 *      `cobranzas` compartía ese recorte y SE LO SACAMOS: ver la nota en su fila. Ahí los cerrados
 *      no inundan porque la pantalla filtra además por saldo pendiente, y sin ellos estaba vacía
 *      de la plata que de verdad se debe.
 *
 *    · En el modelo nuevo eso se resuelve solo, porque la vista filtra por `fulfillment_status`
 *      y un pedido cerrado ya está entregado. Pero los KPIs y la agrupación de esas pantallas
 *      salen de RPCs que todavía reciben `p_stages` y no conocen esa dimensión: si la lista
 *      filtrara por una cosa y el KPI por otra, la pantalla se contradiría a sí misma.
 *
 *    Se corrige junto con las RPCs, en F6. Hasta entonces la diferencia vive acá, escrita, en vez
 *    de estar escondida adentro de cada pantalla.
 */
export const PANTALLAS: Record<string, {
  cola: string;
  vista?: string;
  /** El compromiso que muestra. Es lo que va a la base desde F4. */
  status: readonly OrderStatus[];
  /** @deprecated Vocabulario viejo. Se borra con el enum en F6. */
  stages: readonly OrderLifecycleStage[];
}> = {
  // «Por confirmar» gana los pedidos `online` (checkout sin pagar): son borradores que hasta ahora
  // NINGUNA de las cinco pantallas mostraba — solo se llegaba a ellos desde el L1 «Pedidos». Es uno
  // de los agujeros que el §1 del ADR nombra.
  pedidos:    { cola: 'por_confirmar', status: ['draft'],           stages: STAGES_LEGACY.por_confirmar },
  ventas:     { cola: 'ventas',        status: ['open', 'closed'],  stages: STAGES_LEGACY.ventas },
  canceladas: { cola: 'canceladas',    status: ['cancelled'],       stages: STAGES_LEGACY.canceladas },
  // 🎉 ACÁ SE DISOLVIÓ LA DISCREPANCIA que F4 había tenido que dejar escrita. Estas dos pedían
  //    `['sale','fulfilled']` —una lista recortada a mano de las tres etapas de su cola— y no se
  //    podían alinear sin arrastrar los 11.535 cerrados. En el vocabulario nuevo son `['open']`,
  //    que además QUIERE DECIR ALGO: venta viva, todavía no cerrada. Verificado contra los 15.629
  //    pedidos de producción: la lista vieja y ésta seleccionan exactamente las mismas filas.
  logistica:  { cola: 'ventas', vista: 'por_despachar', status: ['open'], stages: ['sale', 'fulfilled'] },
  /*
   * 🚨 COBRANZAS MIRA TAMBIÉN LOS CERRADOS, Y ES LO QUE LA HACE SERVIR PARA ALGO.
   *
   *    Con `['open']` la pantalla estaba VACÍA de la plata real: los 78 pedidos de Vitalex con
   *    saldo —S/ 1.704— están todos en `closed`, y ninguno entraba. El founder buscaba uno en
   *    particular (Robert, S/ 88, en la agencia de Huancayo desde hace un mes y medio) y la
   *    pantalla no se lo mostraba.
   *
   *    Una venta puede cerrarse debiendo: se entregó, se facturó, se dio por terminada — y el
   *    cliente igual quedó a deber. Que «terminada» borre la deuda de la vista es cómo la plata se
   *    vuelve invisible.
   *
   * 🪤 NO ES LO MISMO QUE `logistica`, aunque compartan cola. Ahí los cerrados SÍ inundan: son
   *    11.535 pedidos y «Por despachar» quedaría inutilizable. Acá no puede pasar, porque esta
   *    pantalla filtra además por `amount_due > 0` (`onlyWithDue`): medido contra producción, la
   *    lista pasa de 0 a 78 filas en Vitalex y a 94 en TODA la plataforma. Un pedido cerrado y
   *    pagado —que son casi todos— no entra.
   */
  cobranzas:  { cola: 'ventas', vista: 'por_cobrar',    status: ['open', 'closed'], stages: ['sale', 'fulfilled'] },
} as const;

/**
 * Qué compromiso pide una pantalla. Es lo que viaja a la base desde F4.
 *
 * Devuelve una COPIA: el registro es la fuente y nadie de afuera debería poder empujarle un valor.
 */
export function statusDePantalla(pantalla: string): OrderStatus[] {
  const p = PANTALLAS[pantalla];
  if (!p) throw new Error(`Pantalla desconocida en el registro de colas: ${pantalla}`);
  return [...p.status];
}

/**
 * @deprecated Vocabulario viejo. Ya nadie debería llamarla: la base traduce sola (`fn_status_de`)
 * y las pantallas piden `statusDePantalla`. Queda hasta F6 para lo que aún no se migró.
 */
export function stagesDePantalla(pantalla: string): OrderLifecycleStage[] {
  const p = PANTALLAS[pantalla];
  if (!p) throw new Error(`Pantalla desconocida en el registro de colas: ${pantalla}`);
  return [...p.stages];
}

/**
 * El filtro de envío que finalmente viaja a la base: el de la cola, cruzado con el del usuario.
 *
 * 🪤 Es una INTERSECCIÓN, no un reemplazo. Si el filtro elegido pisara al de la cola, elegir
 *    «Entregados» dentro de «Por despachar» mostraría pedidos que ya salieron — la pantalla se
 *    escaparía de su propia cola, que es exactamente lo que este ADR persigue. Una vista puede
 *    mirar MENOS que su cola; nunca otra cosa.
 */
export function envioEfectivo(
  base: readonly string[] | undefined,
  elegido: readonly string[],
): string[] {
  if (!base) return [...elegido];
  if (!elegido.length) return [...base];
  return elegido.filter((e) => base.includes(e));
}

// ════════════════════════════════════════════════════════════════════════════════════════════════
// Qué es «trabajo pendiente de despacho»
// ════════════════════════════════════════════════════════════════════════════════════════════════

/**
 * Todos los estados que puede tener un envío. Es la lista que acepta la base.
 *
 * 🪤 Tiene que incluir TODOS. Si falta uno, el pedido que lo tenga desaparece de la cola sin que
 *    nadie lo note — y un pedido pago, sin entregar y fuera de toda lista es el que se pierde: el
 *    cliente llama a los tres días y ahí te enterás.
 */
export const ESTADOS_DE_ENVIO = [
  'none', 'ready', 'registrado', 'cancelled',
  'dispatched', 'in_transit', 'at_agency', 'delivered', 'returned', 'expired',
] as const satisfies readonly OrderShippingStatus[];

/**
 * Si el tipo gana un estado y esta lista no, ESTO NO COMPILA.
 *
 * 🪤 Un test no alcanzaba para esto. El primero que escribí comparaba las dos listas entre sí, o
 *    sea que sólo probaba que fueran coherentes ENTRE ELLAS — y pasaba igual si a las dos les
 *    faltaba el mismo estado. Acá el compilador compara contra el tipo, que es la frontera con la
 *    base, y falla antes de que nadie pueda correr nada.
 */
type EstadoDeEnvioSinClasificar = Exclude<OrderShippingStatus, (typeof ESTADOS_DE_ENVIO)[number]>;
const _todosLosEstadosClasificados: [EstadoDeEnvioSinClasificar] extends [never]
  ? true
  : ['❌ FALTA CLASIFICAR ESTE ESTADO DE ENVÍO EN ESTADOS_DE_ENVIO:', EstadoDeEnvioSinClasificar] = true;
void _todosLosEstadosClasificados;

/**
 * Los envíos que YA SALIERON del almacén.
 *
 * Ésta es la única lista que se mantiene a mano, y es a propósito: se enumera lo que YA SE HIZO,
 * no lo que falta. Así, el día que aparezca un estado nuevo, cae del lado del trabajo pendiente —
 * aparece de más en la cola, alguien lo ve y lo corrige. Al revés, desaparecería en silencio.
 *
 * 🪤 `returned` y `expired` cuentan como «salió»: el paquete se fue y volvió. Eso ya no es un
 *    despacho pendiente, es una incidencia, y vive en Entregas › Envíos.
 */
export const ENVIOS_QUE_YA_SALIERON: readonly string[] = [
  'dispatched', 'in_transit', 'at_agency', 'delivered', 'returned', 'expired',
];

/**
 * La cola de despacho, como la definen Shopify, Odoo y SAP: **el trabajo que queda**, no el estado
 * del documento de envío.
 *
 * La diferencia se ve con el envío ANULADO. Si se anula una guía, el cliente sigue sin su producto:
 * el trabajo volvió a estar pendiente y el pedido tiene que reaparecer en la cola. Shopify lo hace
 * automáticamente (los ítems vuelven a «unfulfilled»), Odoo deja la venta en «falta entregar», y en
 * SAP la cantidad vuelve a la lista de despacho. Ninguno deja un pedido cobrado, sin entregar y
 * fuera de toda lista.
 *
 * 🪤 La primera versión enumeraba a mano los tres estados que SÍ entraban ('none','ready',
 *    'registrado') y por eso se comió los anulados. Elegir a dedo qué entra es el mismo error que
 *    este ADR corrige en el modelo del pedido, cometido un nivel más abajo.
 */
export function envioPendienteDeDespacho(): string[] {
  return ESTADOS_DE_ENVIO.filter((e) => !ENVIOS_QUE_YA_SALIERON.includes(e));
}
